import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const CoreValues = () => {
  const [expandedValue, setExpandedValue] = useState(null);

  const values = [
    {
      id: 'excellence',
      title: 'Technical Excellence',
      icon: 'Code2',
      color: 'from-primary to-accent',
      shortDesc: 'Uncompromising quality in every line of code we write.',
      fullDesc: `Our commitment to technical excellence goes beyond just writing code. We follow industry best practices, conduct thorough code reviews, and continuously update our skills with the latest technologies. Every project undergoes rigorous testing and optimization to ensure peak performance and maintainability.`,
      benefits: [
        'Clean, maintainable code architecture',
        'Comprehensive testing and quality assurance',
        'Performance optimization and monitoring',
        'Future-proof technology choices'
      ],
      metrics: { codeQuality: 'A+', testing: 'Rigorous', uptime: '99.9%' }
    },
    {
      id: 'innovation',
      title: 'Accessible Innovation',
      icon: 'Lightbulb',
      color: 'from-accent to-success',
      shortDesc: 'Making cutting-edge technology affordable for every business.',
      fullDesc: `We believe innovation shouldn't be limited by budget constraints. Through smart technology choices, efficient development processes, and strategic partnerships, we make enterprise-level solutions accessible to startups and growing businesses. Our approach democratizes premium digital experiences.`,
      benefits: [
        'Enterprise features at startup prices', 'Scalable solutions that grow with you', 'Latest technology without premium costs', 'Transparent, value-driven pricing'
      ],
      metrics: { savings: 'Up to 60%', techStack: '15+', value: 'Maximum' }
    },
    {
      id: 'partnership', title: 'Reliable Partnership', icon: 'Handshake', color: 'from-success to-primary', shortDesc: 'Building lasting relationships through trust and transparency.', fullDesc: `We're not just service providers; we're your technology partners. Our approach is built on open communication, transparent processes, and genuine care for your success. We provide ongoing support, strategic guidance, and adapt to your evolving needs throughout our partnership.`,
      benefits: [
        '24/7 support and maintenance', 'Transparent project communication', 'Strategic technology consulting', 'Long-term relationship focus'
      ],
      metrics: { availability: '24/7', response: '<2hrs', transparency: '100%' }
    }
  ];

  const toggleExpanded = (valueId) => {
    setExpandedValue(expandedValue === valueId ? null : valueId);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-muted/30 to-background">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-success/10 text-success px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Star" size={16} />
            <span>Core Values</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            What Drives Us Forward
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Our values aren't just words on a wall—they're the principles that guide every decision,
            every line of code, and every client interaction.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {values.map((value, index) => (
            <motion.div
              key={value.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-lg hover:shadow-brand-lg transition-all duration-300">
                {/* Header */}
                <div className="p-8 pb-6">
                  <div className={`w-16 h-16 bg-gradient-to-br ${value.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon name={value.icon} size={24} color="white" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-4">{value.title}</h3>
                  <p className="text-text-secondary leading-relaxed">{value.shortDesc}</p>
                </div>

                {/* Expandable Content */}
                <div className="px-8">
                  <button
                    onClick={() => toggleExpanded(value.id)}
                    className="flex items-center justify-between w-full py-4 text-left border-t border-border hover:text-primary transition-colors duration-300"
                  >
                    <span className="font-medium">Learn More</span>
                    <motion.div
                      animate={{ rotate: expandedValue === value.id ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Icon name="ChevronDown" size={20} />
                    </motion.div>
                  </button>

                  <AnimatePresence>
                    {expandedValue === value.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="pb-8"
                      >
                        <p className="text-text-secondary mb-6 leading-relaxed">
                          {value.fullDesc}
                        </p>

                        <div className="space-y-3 mb-6">
                          <h4 className="font-semibold text-foreground">Key Benefits:</h4>
                          {value.benefits.map((benefit, idx) => (
                            <div key={idx} className="flex items-start space-x-3">
                              <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                              <span className="text-sm text-text-secondary">{benefit}</span>
                            </div>
                          ))}
                        </div>

                        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
                          {Object.entries(value.metrics).map(([key, val]) => (
                            <div key={key} className="text-center">
                              <div className="text-lg font-bold text-primary">{val}</div>
                              <div className="text-xs text-text-secondary capitalize">{key}</div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Values in Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-success/10 rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
              Our Commitment to You
            </h3>
            <p className="text-lg text-text-secondary mb-8 max-w-3xl mx-auto">
              These values guide every decision we make, every line of code we write, and every client relationship we build. We're ready to prove our commitment through exceptional work.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">100%</div>
                <div className="text-sm text-text-secondary">Commitment to Quality</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent mb-2">Ready</div>
                <div className="text-sm text-text-secondary">To Deliver Excellence</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-success mb-2">Always</div>
                <div className="text-sm text-text-secondary">Transparent & Honest</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CoreValues;